import React, { useEffect, useMemo } from "react";

import { useTranslation } from "utils";

import { AllCropsPracticeDataByYear, CarbonProgram } from "models";

import { Button, CheckboxSegmented, Flex, Typography } from "components";

import { getAllFieldsDataTableRowApplicability } from "pages/app/all-fields-data-table-view/utils/AllFieldsDataTableUtils";

export type AllFieldsDataTableFiltersState = {
  appliedCropIds: string[];
  previouslyAppliedCropIds: string[];
  missingGrowingPeriod: boolean;
  missingSeedings: boolean;
  missingTillages: boolean;
  missingOrganicFertilizers: boolean;
  missingMineralFertilizers: boolean;
  missingYield: boolean;
};

// persisted shape for the per-farm localStorage helper — only the missing-type checkboxes are persisted here.
// appliedCropIds is single-sourced from the shared fieldFilterOptions key (so main/cover crop selection stays in
// sync with Timeline/Map view), and previouslyAppliedCropIds is intentionally never persisted at all — it always
// starts empty on mount, whether that's from switching views or a plain reload
export type AllFieldsDataTableFiltersData = Omit<AllFieldsDataTableFiltersState, "appliedCropIds" | "previouslyAppliedCropIds"> & { farmId: string };

export const AllFieldsDataTableFilters = ({
  allCropsPracticeDataByYear,
  carbonProgram,
  selectedYear,
  filters,
  setFilters,
  onClearAll,
  hasNoFields,
}: {
  allCropsPracticeDataByYear: AllCropsPracticeDataByYear;
  carbonProgram: CarbonProgram | null;
  selectedYear: number | undefined;
  filters: AllFieldsDataTableFiltersState;
  setFilters: React.Dispatch<React.SetStateAction<AllFieldsDataTableFiltersState>>;
  onClearAll: () => void;
  hasNoFields: boolean;
}) => {
  const { t } = useTranslation(["allFieldsDataTableView", "historicalPracticeDataTableView"]);

  const applicablePractices = useMemo(() => {
    if (!selectedYear) {
      return { seedings: false, tillages: false, organicFertilizers: false, mineralFertilizers: false, yield: false };
    }

    const entries = allCropsPracticeDataByYear[selectedYear] ?? [];
    const applicabilityByEntry = entries.map((entry) =>
      getAllFieldsDataTableRowApplicability(
        entry.crop,
        carbonProgram,
        (entry.assignedPractices.mineralFertilizers?.length ?? 0) > 0,
        entry.crop.yieldPerHa != null
      )
    );

    return {
      seedings: applicabilityByEntry.some((applicability) => applicability.seedings),
      tillages: applicabilityByEntry.some((applicability) => applicability.tillages),
      organicFertilizers: applicabilityByEntry.some((applicability) => applicability.organicFertilizers),
      mineralFertilizers: applicabilityByEntry.some((applicability) => applicability.mineralFertilizers),
      yield: applicabilityByEntry.some((applicability) => applicability.yield),
    };
  }, [selectedYear, allCropsPracticeDataByYear, carbonProgram]);

  // prevent hidden toggles from remaining active when selected year or applicable practices change
  const { seedings: reqSeedings, tillages: reqTillages, organicFertilizers: reqOrganic, mineralFertilizers: reqMineral, yield: reqYield } = applicablePractices;
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      missingSeedings: reqSeedings ? prev.missingSeedings : false,
      missingTillages: reqTillages ? prev.missingTillages : false,
      missingOrganicFertilizers: reqOrganic ? prev.missingOrganicFertilizers : false,
      missingMineralFertilizers: reqMineral ? prev.missingMineralFertilizers : false,
      missingYield: reqYield ? prev.missingYield : false,
    }));
  }, [selectedYear, reqSeedings, reqTillages, reqOrganic, reqMineral, reqYield, setFilters]);

  const hasAnyFiltersApplied =
    filters.appliedCropIds.length > 0 ||
    filters.previouslyAppliedCropIds.length > 0 ||
    filters.missingGrowingPeriod ||
    filters.missingOrganicFertilizers ||
    filters.missingMineralFertilizers ||
    filters.missingSeedings ||
    filters.missingTillages ||
    filters.missingYield;

  return (
    <Flex className="historical-practice-data-table-view-filters-bar" align="center" gap="0.5rem">
      <Flex className="historical-practice-data-table-view-filters-left" align="center" gap="0.5rem" wrap="wrap">
        <Typography variant="paragraph-m" color="grey-600" noWrap className="margin-0">
          {t("allFieldsDataTableView:filters.showMissing")}
        </Typography>
        <CheckboxSegmented
          size="small"
          label={t("historicalPracticeDataTableView:table.header.filters.growingPeriod")}
          checked={filters.missingGrowingPeriod}
          disabled={!selectedYear || hasNoFields}
          onChange={() => setFilters((prev) => ({ ...prev, missingGrowingPeriod: !prev.missingGrowingPeriod }))}
        />
        {applicablePractices.seedings && (
          <CheckboxSegmented
            size="small"
            label={t("historicalPracticeDataTableView:table.header.filters.seedings")}
            checked={filters.missingSeedings}
            onChange={() => setFilters((prev) => ({ ...prev, missingSeedings: !prev.missingSeedings }))}
          />
        )}
        {applicablePractices.tillages && (
          <CheckboxSegmented
            size="small"
            label={t("historicalPracticeDataTableView:table.header.filters.tillages")}
            checked={filters.missingTillages}
            onChange={() => setFilters((prev) => ({ ...prev, missingTillages: !prev.missingTillages }))}
          />
        )}
        {applicablePractices.organicFertilizers && (
          <CheckboxSegmented
            size="small"
            label={t("historicalPracticeDataTableView:table.header.filters.organicFertilizers")}
            checked={filters.missingOrganicFertilizers}
            onChange={() => setFilters((prev) => ({ ...prev, missingOrganicFertilizers: !prev.missingOrganicFertilizers }))}
          />
        )}
        {applicablePractices.mineralFertilizers && (
          <CheckboxSegmented
            size="small"
            label={t("historicalPracticeDataTableView:table.header.filters.mineralFertilizers")}
            checked={filters.missingMineralFertilizers}
            onChange={() => setFilters((prev) => ({ ...prev, missingMineralFertilizers: !prev.missingMineralFertilizers }))}
          />
        )}
        {applicablePractices.yield && (
          <CheckboxSegmented
            size="small"
            label={t("allFieldsDataTableView:filters.missingYield")}
            checked={filters.missingYield}
            onChange={() => setFilters((prev) => ({ ...prev, missingYield: !prev.missingYield }))}
          />
        )}
        {hasAnyFiltersApplied && (
          <Button type="text" className="historical-practice-data-table-view-filters-clear-all" onClick={onClearAll} size="small">
            {t("historicalPracticeDataTableView:table.header.filters.clearAll")}
          </Button>
        )}
      </Flex>
    </Flex>
  );
};
